import { useCallback, useState, type FC, type ReactNode } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import type { PageOfSampleTestDto, ShipmentTaskSummaryDto } from '@/api';
import { Alert, Button, Skeleton, cn } from '@/ui';

import { TopbarActions } from '../../components/Topbar';
import { resolveApiErrorMessage } from '../../lib/apiError';
import { formatMoney, toNumber } from '../../lib/format';
import { ShipmentEditorDialog } from '../shipment-actions';
import {
  useDelayedShipments,
  useDueSampleTests,
  useTaskSummary,
  useTasks,
} from './api/queries';
import { DelayedShipments } from './DelayedShipments';
import { DueSampleTests } from './DueSampleTests';
import { resolveTasksSearch, type TasksSearch } from './lib/searchParams';
import { TaskList } from './TaskList';

const ROUTE_ID = '/_app/dashboard' as const;
const ROUTE_PATH = '/dashboard' as const;

const TILE =
  'block rounded-lg border border-l-[3px] border-ink-200 bg-white px-4 py-3.5 text-left hover:border-ink-300';

/*
 * «Мои задачи» - Dashboard макета (v4): показатели, задачи на сегодня, задержанные,
 * «Узнать результат теста». Всё - на живом API: задачи и задержанные - страницы
 * отгрузок, показатели и счётчики - сводка сервера, тесты образцов - их поиск с
 * гранью «пора». Клиенты за менеджерами не закреплены, поэтому «мои» - все
 * клиенты. Ротации в данных нет - её место (четвёртый показатель, нижний блок)
 * занимают тесты образцов.
 */
export const TasksPage: FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: ROUTE_ID });
  const resolved = resolveTasksSearch(search);
  const [isCreating, setIsCreating] = useState(false);

  const summary = useTaskSummary(undefined);
  const partnerSummary = useTaskSummary(resolved.partner);
  const tasks = useTasks(resolved);
  const delayed = useDelayedShipments();
  const dueSampleTests = useDueSampleTests();

  /** Новый клиент или статус - с первой страницы. */
  const setSearch = useCallback(
    (patch: Partial<TasksSearch>) => {
      void navigate({
        from: ROUTE_PATH,
        to: ROUTE_PATH,
        search: (previous: TasksSearch) => ({
          ...previous,
          ...patch,
          page: undefined,
        }),
      });
    },
    [navigate],
  );

  const goToPage = (page: number) => {
    void navigate({
      from: ROUTE_PATH,
      to: ROUTE_PATH,
      search: (previous: TasksSearch) => ({
        ...previous,
        page: page === 1 ? undefined : page,
      }),
    });
  };

  const error =
    summary.error ?? tasks.error ?? delayed.error ?? dueSampleTests.error;

  return (
    <>
      <TopbarActions>
        <Button variant="primary" onClick={() => setIsCreating(true)}>
          <Plus aria-hidden /> Новая отгрузка
        </Button>
      </TopbarActions>

      {error !== null && (
        <Alert tone="error" className="mb-4">
          Не удалось загрузить задачи: {resolveApiErrorMessage(error)}.
        </Alert>
      )}

      <SummaryTiles
        summary={summary.data}
        dueSampleTests={dueSampleTests.data}
        isPaymentSelected={resolved.status === 'Payment'}
        onPaymentClick={() =>
          setSearch({
            status: resolved.status === 'Payment' ? undefined : 'Payment',
          })
        }
      />

      <TaskList
        resolved={resolved}
        tasks={tasks.data}
        isPending={tasks.isPending}
        counts={partnerSummary.data}
        partners={summary.data?.taskPartners ?? []}
        onPartnerChange={(partner) => setSearch({ partner })}
        onStatusChange={(status) => setSearch({ status })}
        onPageChange={goToPage}
      />

      <DelayedShipments page={delayed.data} />

      <DueSampleTests
        page={dueSampleTests.data}
        isPending={dueSampleTests.isPending}
      />

      {isCreating && (
        <ShipmentEditorDialog onClose={() => setIsCreating(false)} />
      )}
    </>
  );
};

/*
 * Показатели - .kpi макета: цветная левая граница, подпись, число, пояснение.
 * «В работе» и «Задержек» ведут в список отгрузок, «Ожидают оплаты» включает чип
 * «Оплата» у задач, «Узнать результат теста» (место «Красных ротаций» макета)
 * ведёт в план тестирования с фильтром «Пора звонить».
 */
const SummaryTiles: FC<{
  summary: ShipmentTaskSummaryDto | undefined;
  dueSampleTests: PageOfSampleTestDto | undefined;
  isPaymentSelected: boolean;
  onPaymentClick: () => void;
}> = ({ summary, dueSampleTests, isPaymentSelected, onPaymentClick }) => {
  if (summary === undefined || dueSampleTests === undefined)
    return (
      <div className="mb-[22px] grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
        {['active', 'delayed', 'payment', 'samples'].map((key) => (
          <div key={key} className={cn(TILE, 'border-l-ink-200')}>
            <Skeleton className="h-3 w-28" />
            <Skeleton className="mt-2 h-6 w-10" />
            <Skeleton className="mt-2 h-3 w-24" />
          </div>
        ))}
      </div>
    );

  const delayed = toNumber(summary.delayed);
  const dueTests = toNumber(dueSampleTests.totalRecords);
  const awaiting = toNumber(summary.awaitingPayment);
  const overdue = toNumber(summary.overduePayment);
  const amount = formatMoney(toNumber(summary.awaitingPaymentAmount));

  const paymentNote =
    awaiting === 0
      ? 'все счета закрыты'
      : overdue > 0
        ? `${overdue} просрочено · ${amount}`
        : `${amount} к получению`;

  return (
    <div className="mb-[22px] grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
      <Link to="/shipments" className={cn(TILE, 'border-l-st-preparing')}>
        <TileContent
          label="Отгрузок в работе"
          value={toNumber(summary.active)}
          note="по всем клиентам"
        />
      </Link>
      <Link
        to="/shipments"
        search={{ delayed: true }}
        className={cn(TILE, 'border-l-st-delayed')}
      >
        <TileContent
          label="Задержек"
          value={delayed}
          note={delayed > 0 ? 'требуют внимания' : 'всё по плану'}
          isDanger={delayed > 0}
        />
      </Link>
      <button
        type="button"
        aria-pressed={isPaymentSelected}
        onClick={onPaymentClick}
        className={cn(
          TILE,
          'cursor-pointer border-l-st-payment',
          isPaymentSelected && 'bg-st-payment-bg/40',
        )}
      >
        <TileContent
          label="Ожидают оплаты"
          value={awaiting}
          note={paymentNote}
          isDanger={overdue > 0}
        />
      </button>
      <Link
        to="/sample-tests"
        search={{ due: true }}
        className={cn(TILE, 'border-l-st-approval')}
      >
        <TileContent
          label="Узнать результат теста"
          value={dueTests}
          note={dueTests > 0 ? 'ожидают звонка' : 'звонить некому'}
        />
      </Link>
    </div>
  );
};

const TileContent: FC<{
  label: string;
  value: number;
  note: ReactNode;
  isDanger?: boolean;
}> = ({ label, value, note, isDanger = false }) => (
  <>
    <span className="block text-xs text-ink-500">{label}</span>
    <span
      className={cn(
        'mt-0.5 block text-[22px] font-semibold tracking-[-0.02em]',
        isDanger ? 'text-st-delayed' : 'text-ink-900',
      )}
    >
      {value}
    </span>
    <span className="mt-0.5 block text-xs text-ink-500">{note}</span>
  </>
);
